import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { 
  TrendingUp, Users, Building, FileText, Calendar, RefreshCw, Loader2,
  ArrowUpRight, ArrowDownRight, Activity
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#62D84E', '#1B3240', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6'];

const AnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/analytics?days=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load analytics data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, change, changeType, icon: Icon, color }) => (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">{title}</p>
            <p className="text-3xl font-bold mt-1">{value}</p>
            {change !== undefined && (
              <div className={`flex items-center mt-2 text-sm ${
                changeType === 'positive' ? 'text-green-600' : 
                changeType === 'negative' ? 'text-red-600' : 'text-gray-500'
              }`}>
                {changeType === 'positive' ? (
                  <ArrowUpRight className="w-4 h-4 mr-1" />
                ) : changeType === 'negative' ? (
                  <ArrowDownRight className="w-4 h-4 mr-1" />
                ) : null}
                <span>{change}% vs période précédente</span>
              </div>
            )}
          </div>
          <div className={`w-12 h-12 rounded-full flex items-center justify-center`} style={{ backgroundColor: `${color}20` }}>
            <Icon className="w-6 h-6" style={{ color }} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading && !data) {
    return (
      <div className="p-8 flex justify-center items-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#1B3240' }}>Analytics Dashboard</h1>
          <p className="text-gray-600 mt-2">Platform performance and growth metrics</p>
        </div>
        <div className="flex gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchAnalytics} variant="outline" disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {data && (
        <div className="space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard 
              title="Total Users" 
              value={data.totals?.users || 0}
              change={data.changes?.users}
              changeType={data.changes?.users > 0 ? 'positive' : data.changes?.users < 0 ? 'negative' : 'neutral'}
              icon={Users}
              color="#62D84E"
            />
            <StatCard 
              title="Companies" 
              value={data.totals?.companies || 0}
              change={data.changes?.companies}
              changeType={data.changes?.companies > 0 ? 'positive' : 'neutral'}
              icon={Building}
              color="#3B82F6"
            />
            <StatCard 
              title="Reservations" 
              value={data.totals?.reservations || 0}
              change={data.changes?.reservations}
              changeType={data.changes?.reservations > 0 ? 'positive' : 'neutral'}
              icon={Calendar}
              color="#F59E0B"
            />
            <StatCard 
              title="Contracts Signed" 
              value={data.totals?.contracts_signed || 0}
              change={data.changes?.contracts}
              changeType={data.changes?.contracts > 0 ? 'positive' : 'neutral'}
              icon={FileText}
              color="#8B5CF6"
            />
          </div>

          {/* Charts Row 1 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* User Growth Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <TrendingUp className="w-5 h-5 mr-2" style={{ color: '#62D84E' }} />
                  User Growth
                </CardTitle>
                <CardDescription>New users over time</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={data.timeline?.users || []}>
                    <defs>
                      <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#62D84E" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#62D84E" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Area 
                      type="monotone" 
                      dataKey="count" 
                      stroke="#62D84E" 
                      fillOpacity={1} 
                      fill="url(#colorUsers)"
                      name="New Users"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Reservations Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Calendar className="w-5 h-5 mr-2" style={{ color: '#F59E0B' }} />
                  Reservations Trend
                </CardTitle>
                <CardDescription>Created vs Accepted reservations</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={data.timeline?.reservations || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="created" fill="#F59E0B" name="Created" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="accepted" fill="#62D84E" name="Accepted" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row 2 */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* User Distribution Pie */}
            <Card>
              <CardHeader>
                <CardTitle>User Distribution</CardTitle>
                <CardDescription>By role</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={data.distribution?.users || []}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {(data.distribution?.users || []).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Reservation Status */}
            <Card>
              <CardHeader>
                <CardTitle>Reservation Status</CardTitle>
                <CardDescription>Current distribution</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={data.distribution?.reservations || []}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {(data.distribution?.reservations || []).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Contract Status */}
            <Card>
              <CardHeader>
                <CardTitle>Contract Status</CardTitle>
                <CardDescription>Signature progress</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={data.distribution?.contracts || []}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {(data.distribution?.contracts || []).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Activity Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Activity className="w-5 h-5 mr-2" style={{ color: '#1B3240' }} />
                Platform Activity
              </CardTitle>
              <CardDescription>Daily active users and actions</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={data.timeline?.activity || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="active_users" 
                    stroke="#62D84E" 
                    strokeWidth={2}
                    dot={{ fill: '#62D84E', r: 4 }}
                    name="Active Users"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="actions" 
                    stroke="#3B82F6" 
                    strokeWidth={2}
                    dot={{ fill: '#3B82F6', r: 4 }}
                    name="Total Actions"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Top Metrics */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Most Active Companies */}
            <Card>
              <CardHeader>
                <CardTitle>Most Active Companies</CardTitle>
                <CardDescription>By reservations and collaborations</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(data.top?.companies || []).slice(0, 5).map((company, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-sm font-medium text-blue-600">
                          {index + 1}
                        </span>
                        <div>
                          <p className="font-medium">{company.name}</p>
                          <p className="text-xs text-gray-500">{company.type}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{company.activity_score}</p>
                        <p className="text-xs text-gray-500">activity score</p>
                      </div>
                    </div>
                  ))}
                  {(!data.top?.companies || data.top.companies.length === 0) && (
                    <p className="text-center text-gray-500 py-4">No data available</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Top Resources */}
            <Card>
              <CardHeader>
                <CardTitle>Most Requested Resources</CardTitle>
                <CardDescription>By reservation count</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(data.top?.resources || []).slice(0, 5).map((resource, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-sm font-medium text-green-600">
                          {index + 1}
                        </span>
                        <div>
                          <p className="font-medium">{resource.name}</p>
                          <p className="text-xs text-gray-500">{resource.role}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{resource.reservations}</p>
                        <p className="text-xs text-gray-500">reservations</p>
                      </div>
                    </div>
                  ))}
                  {(!data.top?.resources || data.top.resources.length === 0) && (
                    <p className="text-center text-gray-500 py-4">No data available</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
